<template>
    <div class="base-carousel">
        <div
            ref="swiperContainer"
            class="swiper-container"
            v-test:swiperContainer
        >
            <div class="swiper-wrapper" v-test:swiperWrapper>
                <template v-for="name in slideSlots">
                    <div class="swiper-slide" v-test:swiperSlide>
                        <slot :name="name" />
                    </div>
                </template>
            </div>
        </div>
        <div
            v-if="$scopedSlots.pagination"
            class="pagination-container"
            v-test:paginationContainer
        >
            <slot
                name="pagination"
                :active="activeIndex"
                :total="slideSlots.length"
            />
        </div>
        <div
            v-if="navigation"
            class="navigation-container"
            :class="{ 'always-show': alwaysShowNavigation }"
            v-test:navigationContainer
        >
            <div
                class="prev-btn"
                v-test:prevBtn
            >
                <b-button
                    ref="prevBtn"
                    variant="primary"
                    class="navigation-btn"
                >
                    <app-icon name="arrow-left" />
                </b-button>
            </div>
            <div
                class="next-btn"
                v-test:nextBtn
            >
                <b-button
                    ref="nextBtn"
                    variant="primary"
                    class="navigation-btn"
                >
                    <app-icon name="arrow-right" />
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
    import Swiper, { Navigation } from 'swiper/swiper-bundle';
    import 'swiper/swiper-bundle.css';

    export default {
        name: 'base-carousel',

        props: {
            breakpoints: {
                type: Object,
                default: () => ({}),
            },

            navigation: {
                type: Boolean,
                default: false,
            },

            spaceBetween: {
                type: Number,
                default: 24,
            },

            // Set to true to show navigation controls on all screen sizes.
            alwaysShowNavigation: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                activeIndex: 0,
            };
        },

        computed: {
            /**
             * Return the slide slots.
             *
             * @returns {string[]}
             */
            slideSlots() {
                return Object.keys(this.$slots).filter((name) => ![
                    'pagination',
                ].includes(name));
            },
        },

        mounted() {
            if (this.navigation) {
                Swiper.use([Navigation]);
            }

            this.swiper = new Swiper(this.$refs.swiperContainer, {
                spaceBetween: this.spaceBetween,
                slidesPerView: 1,
                grabCursor: true,
                breakpoints: Object.keys(this.breakpoints).length
                    ? this.breakpoints
                    : {
                        0: {
                            slidesPerView: 1,
                        },
                        576: {
                            slidesPerView: 2,
                        },
                        960: {
                            slidesPerView: 3.2,
                        },
                        1344: {
                            slidesPerView: 4.2,
                        },
                    },
                navigation: this.navigation
                    ? {
                        nextEl: this.$refs.nextBtn,
                        prevEl: this.$refs.prevBtn,
                    }
                    : false,
            });

            this.swiper.on('slideChange', (swiper) => {
                this.activeIndex = swiper.activeIndex;
            });
        },
    };
</script>

<style lang="scss" scoped>
    .swiper-container {
        overflow: hidden;
    }

    .navigation-container {
        display: flex;
        justify-content: center;
        margin-top: 20px;
        align-items: center;

        &:not(.always-show) {
            @include media-breakpoint-up(md) {
                display: none;
            }
        }
    }

    .navigation-btn {
        min-width: auto;
        border-radius: 50%;
        background-color: secondary-palette(8);
        border: solid 1px primary-palette(2);
        width: 40px;
        height: 40px;
        margin: 0 16px;
        padding: 6px 10px;

        svg {
            fill: primary-palette(2);
        }

        &:hover {
            border-color: primary-palette(1);
            background-color: primary-palette(1);

            svg {
                fill: secondary-palette(8);
            }
        }

        &:focus-visible {
            background-color: primary-palette(1);

            svg {
                fill: secondary-palette(8);
            }
        }
    }

    .btn.btn-primary:not(:disabled):not(.disabled):focus {
        border-color: primary-palette(1);
        background-color: primary-palette(1);

        svg {
            fill: secondary-palette(8);
        }
    }

    .swiper-button-disabled {
        .navigation-btn {
            border-color: secondary-palette(5);
            pointer-events: none;

            svg {
                fill: secondary-palette(5);
            }
        }
    }
</style>
